/* Copyright (c) 2017 Backpack Health LLC */

/* eslint-disable no-underscore-dangle */
import * as _ from 'lodash';

import DateUtil from '../utilities/DateUtil';
import { RGB } from '../utilities/types';

const defaultColor: RGB = {
  r: 125,
  g: 182,
  b: 152,
};

export enum ProfileAction {
  profile_name = 'profile_name',
  profile_dob = 'profile_dob',
  profile_gender = 'profile_gender',
  profile_residence = 'profile_residence',
}

function isNonEmptyString(text) {
  return text !== '' && text !== null && text !== undefined;
}

export interface ProfileData {
  id?: number;
  permission?: string;
  _color?: RGB;
  color_theme_id?: number;
  primary?: number;
  has_user?: boolean;
  profile_state_id?: number;
  profile_state_name?: string;
  race?: {
    race_id?: number[];
    identity?: string;
  };
  race_selection?: 'no_answer' | 'selected';
  blood_type_id?: number;

  gender_id?: number;
  birth_sex_id?: number;
  other_gender?: string;
  date_of_birth?: string;

  avatar_initials?: string;
  avatar_photo?: string;

  preferred_name?: string;
  given_names?: string;
  family_names?: string;

  residence_country_id?: number;
  residence_state_id?: number;
  can_self_consent?: boolean;

  created_at?: string;
}

class ProfileModel {

  static getDisplayName(profileData) {
    return profileData.preferred_name || profileData.given_names;
  }

  public data: ProfileData;

  constructor(d = {}) {
    this.data = d;
    this.data._color = this.data._color || defaultColor;
  }

  profileColor() {
    return this.data._color;
  }

  setColorThemeId(colorThemeId) {
    this.data.color_theme_id = colorThemeId;
  }

  getColorThemeId() {
    return this.data.color_theme_id;
  }

  setProfileColor(color) {
    this.data._color = color || defaultColor;
  }

  getDateOfBirth() {
    return this.data.date_of_birth;
  }

  isDependent() {
    return this.data.primary !== 1;
  }

  get hasUser() {
    return this.data.has_user;
  }

  isDateOfBirthComplete() {
    return !!this.data.date_of_birth;
  }

  isGenderComplete() {
    return _.isNumber(this.data.gender_id);
  }

  isBirthSexComplete() {
    return _.isNumber(this.data.birth_sex_id);
  }

  isActive() {
    return this.data.profile_state_name === 'active';
  }

  hasCustomGender() {
    return !!this.data.other_gender;
  }

  hasAvatarPhoto() {
    return !!this.data.avatar_photo;
  }

  greetingName() {
    return this.data.preferred_name || this.data.given_names;
  }

  fullName() {
    const given = this.data.given_names || '';
    const family = this.data.family_names || '';
    let preferred = this.data.preferred_name || '';

    if (given === preferred || _.includes(given.split(' '), preferred)) {
      preferred = '';
    } else if (family === preferred || _.includes(given.split(' '), preferred)) {
      preferred = '';
    }

    if (preferred) {
      preferred = `(${preferred})`;
    }

    return _.compact([given, family, preferred]).join(' ');
  }

  formalName() {
    const given = this.data.given_names || this.data.preferred_name || '';
    const family = this.data.family_names || '';

    return _.compact([given, family]).join(' ');
  }

  cleanNames() {
    this.data.given_names = (this.data.given_names || '').trim();
    this.data.family_names = (this.data.family_names || '').trim();
    this.data.preferred_name = (this.data.preferred_name || '').trim();
  }

  getInitials() {
    return this.data.avatar_initials;
  }

  getAvatarPhotoUrl() {
    return this.data.avatar_photo;
  }

  getBirthSexId() {
    return this.data.birth_sex_id;
  }

  getGenderId() {
    return this.data.gender_id;
  }

  hasPreferredName() {
    return isNonEmptyString(this.data.preferred_name);
  }

  hasGivenOrFamilyName() {
    const hasGivenName = isNonEmptyString(this.data.given_names);
    const hasFamilyName = isNonEmptyString(this.data.family_names);
    return hasGivenName || hasFamilyName;
  }

  hasValidDateOfBirth() {
    return DateUtil.isValidDate('en', this.data.date_of_birth, 'YYYY-M-D');
  }

  hasValidBloodType() {
    return _.isNumber(this.data.blood_type_id);
  }

  hasValidRace() {
    return this.data.race_selection === 'no_answer' ||
      (this.data.race_selection === 'selected' && this.data.race.race_id.length > 0);
  }

  hasValidGender() {
    return this.isGenderComplete();
  }

  hasValidBirthSex() {
    return this.isBirthSexComplete();
  }

  hasValidResidence() {
    return !!this.residenceCountryId;
  }

  get residenceCountryId() {
    return this.data.residence_country_id;
  }

  get residenceStateId() {
    return this.data.residence_state_id;
  }
}

export default ProfileModel;
